import React, { useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const CLOUD_NAME = "dyzndmawd";
const UPLOAD_PRESET = "loomara_kids";
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

/**
 * Signed Cloudinary upload for admin catalogue images.
 *
 * @param {function(string): void} onUploadSuccess - receives secure_url from Cloudinary
 * @param {string} [previewUrl] - optional controlled preview (e.g. last uploaded image)
 * @param {function(): void} [onClear] - called when admin removes the preview
 */
export default function ImageUploader({ onUploadSuccess, previewUrl, onClear }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [localPreview, setLocalPreview] = useState(null);

    const displayUrl = previewUrl || localPreview;

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file (JPEG, PNG, WebP, etc.).");
            return;
        }

        setUploading(true);
        setLocalPreview(URL.createObjectURL(file));

        try {
            const { data } = await api.post("/admin/cloudinary-signature");

            const formData = new FormData();
            formData.append("file", file);
            formData.append("api_key", data.apiKey);
            formData.append("timestamp", String(data.timestamp));
            formData.append("signature", data.signature);
            formData.append("upload_preset", UPLOAD_PRESET);

            const uploadRes = await fetch(CLOUDINARY_UPLOAD_URL, {
                method: "POST",
                body: formData,
            });

            const body = await uploadRes.json().catch(() => ({}));

            if (!uploadRes.ok) {
                const msg = body?.error?.message || body?.message || `Upload failed (${uploadRes.status})`;
                throw new Error(msg);
            }

            const url = body.secure_url;
            if (!url) {
                throw new Error("Cloudinary did not return a secure URL.");
            }

            setLocalPreview(url);
            onUploadSuccess(url);
            toast.success("Image uploaded to Cloudinary");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(formatApiError(detail) || err.message || "Upload failed");
            if (!previewUrl) {
                setLocalPreview(null);
            }
        } finally {
            setUploading(false);
        }
    };

    const clearImage = () => {
        setLocalPreview(null);
        onClear?.();
    };

    return (
        <div className="rounded-2xl border border-sage-300/40 bg-ivory-50/80 p-4" data-testid="image-uploader">
            <p className="text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-3">
                Product image (Cloudinary)
            </p>

            <label
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sage-300/50 px-4 py-6 cursor-pointer transition-colors ${
                    uploading ? "opacity-60 pointer-events-none" : "hover:border-sage-500 hover:bg-ivory-100/80"
                }`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={handleFile}
                    disabled={uploading}
                    data-testid="image-uploader-input"
                />
                {uploading ? (
                    <>
                        <Loader2 className="h-6 w-6 text-sage-600 animate-spin" />
                        <span className="text-sm text-ink-600 text-center">
                            Securing cloud connection &amp; uploading…
                        </span>
                    </>
                ) : (
                    <>
                        <ImagePlus className="h-6 w-6 text-sage-600" strokeWidth={1.5} />
                        <span className="text-sm text-ink-700">Choose image to upload</span>
                        <span className="text-xs text-ink-500">Signed upload · preset loomara_kids</span>
                    </>
                )}
            </label>

            {displayUrl && (
                <div className="mt-4 flex items-start gap-3">
                    <div className="relative h-24 w-24 rounded-xl overflow-hidden bg-ivory-100 border border-sage-300/30 shrink-0">
                        <img
                            src={displayUrl}
                            alt="Upload preview"
                            className="h-full w-full object-cover"
                            data-testid="image-uploader-preview"
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-ink-500 break-all line-clamp-3">{displayUrl}</p>
                        <button
                            type="button"
                            onClick={clearImage}
                            className="mt-2 inline-flex items-center gap-1 text-xs text-blush-500 hover:text-blush-600"
                            data-testid="image-uploader-clear"
                        >
                            <X size={14} /> Remove image
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
